import { Box, Grid, Tabs, Tab, Typography, Tooltip, IconButton } from "@mui/material"
import { CustomTable } from "../../../../../../components"
import { useState, useMemo, createRef, useLayoutEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { useStyles } from "./styles"
import { CardCompletingProgress } from "../../components/cardCompletingProgress"
import { useContextState } from "../../../../../../hooks/theme"
import { Roles } from "../../../../../../utils/enums/roles"
import { getUserRole, getUserId } from "../../../../../../service/auth"
import { FilterHeader } from "../../components/filterHeader"
import { FilterHeaderRef } from "../../components/filterHeader/types"
import { useInput } from "./constants"
import { ObjectGeneric } from "../../../../../../utils/types/generals"
import { currentWeek } from "../methods/calcDates"
import { dashboardService, userServiceFetch } from "../../../../../../utils/constants/callService"
import { formatCompletingQuestionnaires } from "./methods/formatDataToEndpoint"
import { formatResponse } from "./methods/formatEndpointDataToRow"
import BaseModal from "../../../../../../components/modal/baseModal"
import ModalCrudTable from "../../../../../../components/modalTableWithFilter"
import Loader from "../../../../../../components/loader"
import dayjs from "dayjs"
import { Constants } from "../../../../../../utils/constants"
import { es } from "date-fns/locale"
import { format } from "date-fns"
import { getColumns } from "./columns"
import { useNavigate } from "react-router-dom"
import { icons } from "../../../../../../utils/icons"
import { intervaleList } from "../../../../../../utils/constants/intervaleList"
import { RedirectedIds } from "../../../../typing"

const defaultDates = {
  from: currentWeek().start.getTime(),
  to: currentWeek().end.getTime(),
  intervale: intervaleList[0]
}

const storeHeaders = ({ t }: { t: any }) => {
  return [
    {
      id: 'id',
      label: 'ID',
    },
    {
      id: 'nombre',
      label: 'Nombre',
    },
    {
      id: 'gerente',
      label: 'Gerente',
    }
  ]
}

const CompletingQuestionnaires = () => {
  const { t } = useTranslation()
  const styles = useStyles()
  const { colors } = useContextState()
  const userRole = getUserRole()
  const ref = createRef<FilterHeaderRef>()
  const navigate = useNavigate()
  const { input, removeValues, items } = useInput()
  const [tabValue, setTabValue] = useState(0)
  const [loading, setLoading] = useState(false)
  const [tableData, setTableData] = useState<any[]>([])
  const [totalesPorRole, setTotalesPorRole] = useState<any>({})
  const [lastUpdate, setLastUpdate] = useState<string>('')
  const [openModal, setOpenModal] = useState(false)
  const [tableAssociateStore, setTableAssociateStore] = useState<any>({ column: [], row: [] })
  const [isTableStoreLoading, setIsTableStoreLoading] = useState(false)
  const [userTotals, setUserTotals] = useState<any>(null)
  const hasLoadedInitialData = useRef(false)
  const availableTabs = useMemo(() => {
    if (userRole === Roles.ADMIN) {
      return [
        { label: 'Gerente', value: 0, dataIndex: [5] },
        { label: 'Supervisor', value: 1, dataIndex: [2] },
        { label: 'Usuarios de jerarquías', value: 2, dataIndex: [3, 6, 7] }
      ]
    } else if (userRole === Roles.GERENT) {
      return [
        { label: 'Supervisor', value: 0, dataIndex: [2] },
        { label: 'Usuarios de jerarquías', value: 1, dataIndex: [3, 6, 7] }
      ]
    } else if (userRole === Roles.SUPERVISOR) {
      return [
        { label: 'Usuarios de jerarquías', value: 0, dataIndex: [3, 6, 7] }
      ]
    }
    return []
  }, [userRole])

  const handleTabChange = async (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
    const currentTab = availableTabs.find(tab => tab.value === newValue)
    if (currentTab && ref.current?.getValues) {
      const currentFilters = ref.current.getValues()
      const payload = {
        ...currentFilters,
        roleIds: currentTab.dataIndex
      }
      await handleGetData(payload)
    }
  }



  const handleRedirect = (params: any) => {
    const rowData = params.row
    const userId = rowData.userId || rowData.id
    const filterValues = ref.current?.getValues ? ref.current.getValues() : {}
    const dateFrom = filterValues.from || defaultDates.from
    const dateTo = filterValues.to || defaultDates.to
    const formattedFrom = format(new Date(dateFrom), Constants.dateFormat)
    const formattedTo = format(new Date(dateTo), Constants.dateFormat)
    const queryParams = new URLSearchParams({
      users: userId.toString(),
      from: formattedFrom,
      to: formattedTo
    })
    navigate(`/dashboard/reports/programmed?${queryParams.toString()}`)
  }

  const handleActions = async (params: any) => {
    const rowData = params.row
    const userId = rowData.userId || rowData.id
    if (!userId) {
      console.error('No se encontró el ID del usuario', params)
      return
    }
    setOpenModal(true)
    setIsTableStoreLoading(true)
    try {
      const response: any = await userServiceFetch.getAsociateUserStores(userId)
      if (!response.errors && Array.isArray(response)) {
        const formattedData = response.map((store: any) => ({
          id: store.id || 'N/A',
          nombre: store.name || 'N/A',
          gerente: store.userManage
            ? `${store.userManage.firstName || ''} ${store.userManage.lastName || ''}`.trim() || 'N/A'
            : 'N/A',
          keyCode: store.keyCode || 'N/A',
          address: store.address || 'N/A',
          phone: store.phone || 'N/A'
        }))

        setTableAssociateStore({
          column: storeHeaders({ t }),
          row: formattedData
        })
      } else {
        setTableAssociateStore({ column: storeHeaders({ t }), row: [] })
      }
    } catch (error) {
      console.error('Error al obtener las tiendas asociadas:', error)
      setTableAssociateStore({ column: storeHeaders({ t }), row: [] })
    } finally {
      setIsTableStoreLoading(false)
    }
  }

  const columns = getColumns(colors, handleRedirect, handleActions, t)

  const getUserTotals = async (payload: ObjectGeneric) => {
    const userId = getUserId()
    if (!userId || (userRole !== Roles.GERENT && userRole !== Roles.SUPERVISOR)) {
      setUserTotals(null)
      return
    }

    try {
      const { body } = formatCompletingQuestionnaires(payload)
      const isDailyInterval = payload?.intervale?.value === '1d'
      const response = isDailyInterval
        ? await dashboardService.CompletingQuestionnaires({ body })
        : await dashboardService.CompletingQuestionnairesWeek({ body })
      const { data } = formatResponse(response)
      const userRow = data.find(row => row.userId === userId)
      if (userRow) {
        setUserTotals({
          totalProgramado: userRow.schedules || 0,
          totalNoEjecutado: userRow.notExecuted || 0,
          totalFinalizado: userRow.finished || 0,
          promedioResultado: parseFloat(userRow.result) || 0
        })
      } else {
        setUserTotals(null)
      }
    } catch (error) {
      setUserTotals(null)
    }
  }

  const handleGetData = async (payload: ObjectGeneric) => {
    setLoading(true)
    try {
      const currentTab = availableTabs.find(tab => tab.value === tabValue)
      const finalPayload = {
        ...payload,
        roleIds: payload.roleIds || (currentTab ? currentTab.dataIndex : [])
      }

      const { body } = formatCompletingQuestionnaires(finalPayload)
      const isDailyInterval = finalPayload?.intervale?.value === '1d'
      const response = isDailyInterval
        ? await dashboardService.CompletingQuestionnaires({ body })
        : await dashboardService.CompletingQuestionnairesWeek({ body })
      const { data, totalesPorRole: totales } = formatResponse(response)
      setTableData(data)
      setTotalesPorRole(totales || {})
      setLastUpdate(Date.now().toString())
      await getUserTotals(finalPayload)
    } catch (error) {
      setTableData([])
      setTotalesPorRole({})
    } finally {
      setLoading(false)
    }
  }

  const callGetData = (payload?: { dafaultValue: ObjectGeneric }) => {
    const defaultValues: ObjectGeneric = payload?.dafaultValue || (ref.current?.getValues ? ref.current?.getValues() : {})

    const currentTab = availableTabs.find(tab => tab.value === tabValue)
    const finalPayload = {
      ...defaultValues,
      roleIds: currentTab ? currentTab.dataIndex : []
    }

    handleGetData(finalPayload)
  }

  const getFilter = (payload: { dafaultValue: ObjectGeneric }) => {
    callGetData(payload)
  }

  const refresh = (payload: { dafaultValue: ObjectGeneric }) => {
    callGetData(payload)
  }

  useLayoutEffect(() => {
    const unitsArray = Array.isArray(items.units) ? items.units : (items.units as any)?.items || []
    if (unitsArray.length && !hasLoadedInitialData.current && availableTabs.length > 0) {
      hasLoadedInitialData.current = true
      const firstTab = availableTabs[0]
      const initialPayload = {
        ...defaultDates,
        roleIds: firstTab.dataIndex
      }
      callGetData({ dafaultValue: initialPayload })
    }
  }, [items.units, availableTabs])

  const getCurrentRoleTotals = () => {
    const currentTab = availableTabs.find(tab => tab.value === tabValue)
    if (!currentTab) return null
    if (currentTab.dataIndex.length > 1) {
      const combinedTotals = currentTab.dataIndex.reduce((acc, roleId) => {
        const roleTotals = totalesPorRole[roleId]
        if (roleTotals) {
          return {
            totalProgramado: (acc.totalProgramado || 0) + (roleTotals.totalProgramado || 0),
            totalNoEjecutado: (acc.totalNoEjecutado || 0) + (roleTotals.totalNoEjecutado || 0),
            totalFinalizado: (acc.totalFinalizado || 0) + (roleTotals.totalFinalizado || 0),
            promedioResultado: (acc.promedioResultado || 0) + (roleTotals.promedioResultado || 0),
          }
        }
        return acc
      }, { totalProgramado: 0, totalNoEjecutado: 0, totalFinalizado: 0, promedioResultado: 0 })
      const rolesWithData = currentTab.dataIndex.filter(roleId => totalesPorRole[roleId])
      if (rolesWithData.length > 0) {
        combinedTotals.promedioResultado = combinedTotals.promedioResultado / rolesWithData.length
      }
      return combinedTotals
    }
    return totalesPorRole[currentTab.dataIndex[0]] || null
  }

  return (
    <Grid item xs={12}>
      <div id={RedirectedIds.COMPLETING_QUESTIONNAIRES} />
      <Box sx={styles.card}>
        <Box sx={styles.header}>
          <Box>
            <Typography variant="h6" sx={styles.titleHeaders}>
              {t('dashboard.indicators.completingQuestionnaires.title')}
            </Typography>
            <Typography sx={styles.dateFooter}>
              {lastUpdate ? `${t('dashboard.indicators.completingQuestionnaires.lastUpdate')} ${tableData?.[0]?.semana ? `${tableData[0].semana} - ` : ''}${format(
                dayjs(parseInt(lastUpdate)).toDate(),
                Constants.humanFormat,
                { locale: es }
              )}` : ''}
            </Typography>
          </Box>
          <Box sx={styles.filterContainer}>
            <FilterHeader
              countDefault={1}
              hiddenRefresh
              cleanFilter={removeValues}
              ref={ref}
              defaultValues={defaultDates}
              filterData={getFilter}
              refresh={refresh}
              title="Filtros"
              input={input}
              externalValidation={() => true}
            />
            <Tooltip title={t('ToolTips.refresh')}>
              <IconButton
                sx={styles.refresh}
                onClick={() => {
                  refresh({ dafaultValue: ref.current?.getValues() || defaultDates })
                }}
              >
                {icons("refresh")}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Box sx={styles.containerCards}>
          {(userRole === Roles.GERENT || userRole === Roles.SUPERVISOR) && userTotals && (
            <CardCompletingProgress
              title={t('dashboard.indicators.completingQuestionnaires.ownScheduling')}
              colorTitle={colors.color2}
              total={userTotals.totalProgramado}
              descriptionTotal={t('dashboard.indicators.completingQuestionnaires.totalPrograming')}
              porcentages={[
                {
                  label: t('dashboard.indicators.completingQuestionnaires.noExecuted'),
                  porcentage: userTotals.totalProgramado > 0
                    ? parseFloat(((userTotals.totalNoEjecutado / userTotals.totalProgramado) * 100).toFixed(1))
                    : 0,
                  numerator: userTotals.totalNoEjecutado,
                  denominator: userTotals.totalProgramado
                },
                {
                  label: t('dashboard.indicators.completingQuestionnaires.finished'),
                  porcentage: userTotals.totalProgramado > 0
                    ? parseFloat(((userTotals.totalFinalizado / userTotals.totalProgramado) * 100).toFixed(1))
                    : 0,
                  numerator: userTotals.totalFinalizado,
                  denominator: userTotals.totalProgramado
                },
                {
                  label: t('dashboard.indicators.completingQuestionnaires.result'),
                  porcentage: userTotals.promedioResultado?.toFixed(1) || 0,
                },
              ]}
            />
          )}

          <CardCompletingProgress
            title={t('dashboard.indicators.completingQuestionnaires.totalgenerals')}
            colorTitle={colors.color2}
            total={getCurrentRoleTotals()?.totalProgramado || 0}
            descriptionTotal={t('dashboard.indicators.completingQuestionnaires.totalPrograming')}
            porcentages={[
              {
                label: t('dashboard.indicators.completingQuestionnaires.noExecuted'),
                porcentage: getCurrentRoleTotals()?.totalProgramado > 0
                  ? parseFloat(((getCurrentRoleTotals()?.totalNoEjecutado / getCurrentRoleTotals()?.totalProgramado) * 100).toFixed(1))
                  : 0,
                numerator: getCurrentRoleTotals()?.totalNoEjecutado || 0,
                denominator: getCurrentRoleTotals()?.totalProgramado || 0
              },
              {
                label: t('dashboard.indicators.completingQuestionnaires.finished'),
                porcentage: getCurrentRoleTotals()?.totalProgramado > 0
                  ? parseFloat(((getCurrentRoleTotals()?.totalFinalizado / getCurrentRoleTotals()?.totalProgramado) * 100).toFixed(1))
                  : 0,
                numerator: getCurrentRoleTotals()?.totalFinalizado || 0,
                denominator: getCurrentRoleTotals()?.totalProgramado || 0
              },
              {
                label: t('dashboard.indicators.completingQuestionnaires.result'),
                porcentage: getCurrentRoleTotals()?.promedioResultado?.toFixed(1) || 0,
              },
            ]}
          />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', overflow: 'hidden' }}>
          <Box sx={{
            ...styles.tabsContainer,
            boxSizing: 'border-box',
            ...(availableTabs.length === 1 && {
              width: '50%',
              '& .MuiTabs-root': {
                backgroundImage: `linear-gradient(to right, ${colors.contrastEndpoint}, ${colors.primaryEndpoint}, ${colors.secondaryEndpoint})`,
                backgroundSize: '200% 100%',
                backgroundPosition: 'left',
                borderRadius: '8px 8px 0 0',
              },
              '& .MuiTab-root.Mui-selected': {
                borderRadius: '8px 8px 0 0',
              }
            })
          }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                '& .MuiTabs-flexContainer': {
                  width: '100%',
                },
                '& .MuiTabs-scroller': {
                  overflow: 'hidden',
                }
              }}
            >
              {availableTabs.map((tab) => (
                <Tab key={tab.value} label={tab.label} />
              ))}
            </Tabs>
          </Box>
          <CustomTable
            loader={loading}
            column={columns}
            row={tableData}
            customHeaderGradient
            firstTabSelected={tabValue === 0}
            selected={-1}

          />
        </Box>
      </Box>
      <BaseModal open={openModal} sx={{ width: '600px', height: '620px' }}>
        {isTableStoreLoading ? (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            minHeight: '500px'
          }}>
            <Loader />
          </Box>
        ) : (
          <ModalCrudTable
            t={t}
            filterRow={(filter: any, list: any[]) => {
              const search = filter.search || ''
              if (!search) return list
              const searchLower = search.toLowerCase()
              return list.filter((row: any) =>
                row.id?.toString().toLowerCase().includes(searchLower) ||
                row.nombre?.toLowerCase().includes(searchLower) ||
                row.gerente?.toLowerCase().includes(searchLower)
              )
            }}
            color={colors}
            exportFiles
            table={tableAssociateStore}
            customeHeader={storeHeaders({ t })}
            initialFilter={{ search: '' }}
            setInformation={(e: any) => { }}
            onClose={() => {
              setOpenModal(false)
              setTableAssociateStore({ column: [], row: [] })
            }}
            filter={{
              filterTitle: '',
              label: "Buscar unidad",
              name: 'search',
              title: t('general.tags.unit'),
              type: 2,
              sx: { width: "100%" }
            }}
          />
        )}
      </BaseModal>
    </Grid>
  )
}

export { CompletingQuestionnaires }
